```
<template>
  <div class="ref-frame-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$观测结果 = 相对运动 + 牵连运动$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <!-- 动态背景层 -->
        <div class="infinite-layers" :style="backgroundStyle">
          <div class="layer star-field"></div>
          <div class="layer grid-lines"></div>
        </div>

        <div class="entities-overlay">
          <!-- 核心车辆 -->
          <div class="player-car" :style="carStyle">
             <div class="car-body-pro">
                <div class="cabin-glass"></div>
                <div class="glow-tail"></div>
             </div>
             <!-- 速度矢量 -->
             <div class="indicator-tag" v-if="referenceFrame === 'ground'">
                <div class="v-line"></div>
                <span>车速 v = 80km/h</span>
             </div>
             <div class="indicator-tag static" v-else>
                <span>车内视角：相对静止</span>
             </div>
          </div>

          <!-- 系统固连物体 (观察者/景物) -->
          <div class="observer-pro" :style="observerStyle">
             <div class="p-man"></div>
             <div class="indicator-tag reverse" v-if="referenceFrame === 'car'">
                <div class="v-line l"></div>
                <span>景物后退 v = -80km/h</span>
             </div>
          </div>
        </div>

        <div class="hud-panel">
           <div class="hud-item blue">
              <label>当前观测点</label>
              <span class="val">{{ referenceFrame === 'ground' ? '地面静止观察者' : '跟随列车观察者' }}</span>
           </div>
        </div>
      </div>

      <!-- 控制面板 -->
      <div class="compact-controls">
        <div class="control-card">
           <div class="control-vertical-group">
              <div class="mode-selector-row">
                 <span class="label">选择参考系：</span>
                 <el-radio-group v-model="referenceFrame" size="small" class="full-row-radio">
                    <el-radio-button label="ground">地面参考系</el-radio-button>
                    <el-radio-button label="car">车内参考系</el-radio-button>
                 </el-radio-group>
              </div>
           </div>
        </div>

        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <p v-if="referenceFrame === 'ground'"><strong>地面视角：</strong>景物不动，车向前跑。这是我们平时在路边看车的习惯。”</p>
           <p v-else><strong>车内视角：</strong>我们在车里看窗外景物飞驰往后，但身边的人是静止的。**运动是相对的**。</p>
        </div>
      </div>
    </div>
  </div>
</template>


<script>
export default {
  name: 'ReferenceFrameViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      referenceFrame: 'ground',
      offset: 0,
      running: true
    }
  },
  computed: {
    backgroundStyle() {
      // 背景速度模拟：地面视角下背景不动，车内视角下背景快速后退
      const speed = this.referenceFrame === 'ground' ? 0 : 2; // 0s for static, 2s for fast movement
      return { '--speed': `${speed}s` };
    },
    carStyle() {
      // 车辆位置模拟：地面视角下车辆前进，车内视角下车辆固定在画面中央
      if (this.referenceFrame === 'ground') {
        // 车辆从左侧进入，向右移动
        const x = (this.offset * 2 % 800) - 150; // Adjust range for car to move across screen
        return { transform: `translateX(${x}px)` };
      } else {
        // 车辆固定在画面中央偏左
        return { transform: `translateX(200px)` };
      }
    },
    observerStyle() {
      // 观察者位置模拟：地面视角下观察者固定，车内视角下观察者（代表地面景物）后退
      if (this.referenceFrame === 'ground') {
        // 观察者固定在画面左侧
        return { transform: `translateX(100px)` };
      } else {
        // 观察者（景物）从右侧进入，向左移动
        const x = (this.offset * 2 % 800) - 150; // Same speed as car in ground frame, but opposite direction
        return { transform: `translateX(${x}px)` };
      }
    }
  },
  watch: {
    knowledgePoint: {
      handler(val) {
        if (/车|内部|动/.test(val)) {
          this.referenceFrame = 'car';
        } else {
          this.referenceFrame = 'ground';
        }
      },
      immediate: true
    }
  },
  methods: {
    animate() {
      if (!this.running) return;
      this.offset += 1;
      requestAnimationFrame(this.animate);
    }
  },
  mounted() {
    this.animate();
  },
  beforeDestroy() {
    this.running = false;
  }
}
</script>

<style scoped lang="scss">
.ref-frame-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 13px; color: #1e293b; font-weight: 500; } .formula-chip { background: #e0f2fe; color: #0284c7; border: 1px solid #bae6fd; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { flex: 1; min-height: 220px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; 
  .infinite-layers { position: absolute; inset: 0; 
    .layer { position: absolute; inset: 0; } 
    .star-field {
      background: radial-gradient(circle at center, rgba(255, 255, 255, 0.1) 1px, transparent 1px);
      background-size: 50px 50px;
      animation: star-move var(--speed) linear infinite;
    }
    .grid-lines { 
      background-image: linear-gradient(rgba(34, 211, 238, 0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(34, 211, 238, 0.05) 1px, transparent 1px); 
      background-size: 40px 40px; 
      mask-image: linear-gradient(to bottom, transparent, black); 
      animation: grid-move var(--speed) linear infinite;
    }
  }
  .hud-panel { position: absolute; top: 12px; left: 12px; .hud-item { background: rgba(15, 23, 42, 0.82); border-radius: 8px; border: 1px solid #1e293b; padding: 4px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; } .val { font-size: 13px; color: #f1f5f9; font-weight: 800; } &.blue { border-left: 3px solid #3b82f6; } } }
}
.entities-overlay { position: absolute; inset: 0; display: flex; align-items: flex-end; padding-bottom: 60px; pointer-events: none; }
.player-car { position: absolute; bottom: 80px; width: 120px; height: 36px; transition: transform 0.05s linear; 
  .car-body-pro { width: 100%; height: 100%; background: linear-gradient(135deg, #3b82f6, #60a5fa); border-radius: 4px 20px 4px 4px; box-shadow: 0 8px 16px rgba(0,0,0,0.5); position: relative; 
    .cabin-glass { position: absolute; top: 4px; left: 60px; width: 45px; height: 12px; background: rgba(255,255,255,0.25); border-radius: 2px 12px 0 0; } 
    .glow-tail { position: absolute; right: 0; bottom: 4px; width: 4px; height: 10px; background: #ef4444; box-shadow: 0 0 10px #ef4444; } 
  } 
  .indicator-tag { position: absolute; top: -35px; width: 120px; text-align: center; color: #3b82f6; font-family: monospace; font-size: 11px; font-weight: 800; 
    .v-line { height: 2px; width: 60px; background: #3b82f6; margin: 4px auto; position: relative; 
      &::after { content: ''; position: absolute; right: 0; top: -3px; width: 6px; height: 6px; border-top: 2px solid #3b82f6; border-right: 2px solid #3b82f6; transform: rotate(45deg); } 
    } 
    &.static { color: #94a3b8; font-weight: 400; font-size: 10px; opacity: 0.6; } 
  } 
}
.observer-pro { position: absolute; bottom: 80px; 
  .p-man { width: 14px; height: 32px; background: #10b981; border-radius: 4px 4px 0 0; } 
  .indicator-tag { position: absolute; top: -35px; width: 140px; left: -63px; text-align: center; color: #ef4444; font-size: 11px; font-family: monospace; font-weight: 800; 
    .v-line.l { height: 2px; width: 60px; background: #ef4444; margin: 4px auto; position: relative; 
      &::after { content: ''; position: absolute; left: 0; top: -3px; width: 6px; height: 6px; border-top: 2px solid #ef4444; border-left: 2px solid #ef4444; transform: rotate(-45deg); } 
    } 
  } 
}
.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; }
.mode-selector-row { display: flex; align-items: center; gap: 10px; .label { font-size: 12px; color: #64748b; font-weight: 700; white-space: nowrap; } .full-row-radio { flex: 1; display: flex; } }
.discovery-card-compact { background: #f0f9ff; padding: 10px 14px; border-radius: 10px; border: 1px solid #e0f2fe; display: flex; align-items: center; gap: 10px; i { color: #0ea5e9; font-size: 1.1rem; } p { flex: 1; margin: 0; font-size: 12px; color: #0369a1; line-height: 1.5; strong { color: #075985; } } }

@keyframes star-move {
  from { background-position: 0 0; }
  to { background-position: -100px 0; } /* Move stars horizontally */
}

@keyframes grid-move {
  from { background-position: 0 0; }
  to { background-position: -80px 0; } /* Move grid lines horizontally */
}
</style>
```
